/**
 * Member Recertification Landing — IG-12 SMS/email link target.
 *
 * Reached from the daily IG-11 alert messages (45/30/7-day Medi-Cal
 * recertification reminders). Link format:
 *   {FRONTEND_URL}/member/eligibility/recertification?member_id=...
 *
 * Auth flow:
 *  - Authenticated member: show personalized recert state (next due date,
 *    last verification, action checklist) via the member-portal session.
 *  - Unauthenticated visitor: show a friendly "your recert is coming due"
 *    landing with a sign-in CTA. Possession of the URL alone is NOT
 *    treated as proof of identity, so no PII is shown until login.
 *
 * Mounted via App.tsx pathname detection (parallel to PublicSurveyPage)
 * so the route works regardless of the member-portal SPA routing state.
 */

import { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Calendar, CheckCircle, AlertTriangle, Loader2, LogIn } from 'lucide-react';
import { getMemberData, getMemberToken } from '@/lib/memberAuth';
import { getWeaveinApiUrl } from '@/src/config';

interface RecertDashboardRow {
  redetermination_id: number;
  member_id: number | null;
  due_date: string;
  days_until_due: number;
  status: string;
  outcome: string | null;
  redetermination_number: number;
  last_eligibility_check_at: string | null;
  last_eligibility_status: string | null;
  reminder_sent_at: string | null;
  warning_sent_at: string | null;
  final_notice_sent_at: string | null;
}

export const MemberRecertificationPage: React.FC = () => {
  const memberIdFromUrl = useMemo(() => {
    if (typeof window === 'undefined') return null;
    const p = new URLSearchParams(window.location.search);
    const raw = p.get('member_id');
    return raw ? Number(raw) : null;
  }, []);

  const memberData = getMemberData();
  const memberToken = getMemberToken();
  const isAuthenticated = !!(memberData && memberToken);
  const memberId = memberData?.id ?? memberIdFromUrl;

  const [row, setRow] = useState<RecertDashboardRow | null>(null);
  const [loading, setLoading] = useState(isAuthenticated);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isAuthenticated || !memberId) {
      setLoading(false);
      return;
    }
    const load = async () => {
      try {
        // Hits IG-10 dashboard endpoint, scoped server-side by cbo_id from
        // the member's session. Returns the active redet row for this
        // member (filtered client-side; the endpoint already org-scopes).
        const url = getWeaveinApiUrl('/api/eligibility/recertification/dashboard?limit=200');
        const r = await fetch(url, {
          headers: {
            Authorization: `Bearer ${memberToken}`,
            'Content-Type': 'application/json',
          },
        });
        if (!r.ok) {
          setError('Could not load your recertification status.');
          return;
        }
        const body = await r.json();
        const rows: RecertDashboardRow[] = (body.rows || []) as RecertDashboardRow[];
        const mine = rows.find((x) => x.member_id === memberId) || null;
        setRow(mine);
      } catch {
        setError('Network error loading your recertification status.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [isAuthenticated, memberId, memberToken]);

  const urgencyClass = (days: number | undefined) => {
    if (days === undefined) return 'text-gray-700';
    if (days < 0) return 'text-red-600';
    if (days <= 7) return 'text-red-600';
    if (days <= 30) return 'text-amber-600';
    return 'text-blue-700';
  };

  // ── Unauthenticated landing ───────────────────────────────────────────
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 py-12 px-4">
        <div className="max-w-xl mx-auto space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-blue-900">
                <Calendar className="w-5 h-5" />
                Your Medi-Cal recertification
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-gray-700">
              <p>
                You received this link because your Medi-Cal recertification is coming due.
                Renewing on time is the only way to keep your coverage active.
              </p>
              <p>Sign in to view your personalized recertification details and submit your renewal.</p>
              <div className="flex flex-col sm:flex-row gap-2 pt-2">
                <Button
                  onClick={() => {
                    const next = window.location.pathname + window.location.search;
                    window.location.href = `/member/login?next=${encodeURIComponent(next)}`;
                  }}
                  className="flex items-center gap-2"
                >
                  <LogIn className="w-4 h-4" />
                  Sign in to your account
                </Button>
              </div>
              <p className="text-xs text-gray-500 pt-2">
                If you need help, contact your care coordinator.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // ── Loading authenticated lookup ──────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-gray-500" />
      </div>
    );
  }

  // ── Authenticated, no recert row ─────────────────────────────────────
  if (!row) {
    return (
      <div className="min-h-screen bg-gray-50 py-12 px-4">
        <div className="max-w-xl mx-auto">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-green-700">
                <CheckCircle className="w-5 h-5" />
                You're all set
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700">
                We don't have an active Medi-Cal recertification on file for you right now.
                If you think this is incorrect, contact your care coordinator.
              </p>
              {error ? <p className="text-xs text-amber-600 mt-2">{error}</p> : null}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // ── Authenticated landing with personalized recert details ────────────
  const days = row.days_until_due;
  const dueLabel = days < 0 ? `${Math.abs(days)} days overdue` : `${days} days from today`;

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-2xl mx-auto space-y-4">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-blue-700" />
              Your Medi-Cal recertification
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-gray-800">
            <div>
              <span className="text-sm text-gray-500">Due date</span>
              <div className={`text-xl font-semibold ${urgencyClass(days)}`}>{row.due_date}</div>
              <div className={`text-sm ${urgencyClass(days)}`}>{dueLabel}</div>
            </div>
            <div>
              <span className="text-sm text-gray-500">Status</span>
              <div className="font-medium">{row.status}</div>
              {row.outcome ? (
                <div className="text-sm text-gray-600">Outcome: {row.outcome}</div>
              ) : null}
            </div>
            {row.last_eligibility_check_at ? (
              <div>
                <span className="text-sm text-gray-500">Last eligibility check</span>
                <div className="text-sm">
                  {new Date(row.last_eligibility_check_at).toLocaleDateString()} —{' '}
                  {row.last_eligibility_status || '—'}
                </div>
              </div>
            ) : null}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">What to do next</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm text-gray-700">
            <p>
              {days < 0
                ? 'Your renewal is overdue. Contact your care coordinator immediately to avoid a coverage gap.'
                : days <= 7
                ? 'Submit your recertification today. Missing the deadline will end your Medi-Cal coverage.'
                : days <= 30
                ? 'Please act soon. We recommend submitting your renewal at least two weeks before the due date.'
                : 'You still have time. Gather your income and household information so you can submit when ready.'}
            </p>
            <ul className="list-disc list-inside text-gray-600 space-y-1">
              <li>Confirm your current income and household size are up to date.</li>
              <li>Upload any documents your care coordinator has requested.</li>
              <li>Reply STOP to any text reminders to opt out.</li>
            </ul>
            {(days < 0 || days <= 7) ? (
              <div className="flex items-center gap-2 text-amber-700 text-xs pt-1">
                <AlertTriangle className="w-4 h-4" />
                Need help? Contact your care coordinator directly.
              </div>
            ) : null}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default MemberRecertificationPage;
